(() => {
  'use strict';
  if (window.ThemeHeroEffects?.initialized) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const deferredVideoHeroes = new Set();
  let videoObserver = null;
  const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

  const getHeroes = (scope = document) => {
    const heroes = [];
    if (scope?.matches?.('[data-hero-section]')) heroes.push(scope);
    if (scope?.querySelectorAll) heroes.push(...scope.querySelectorAll('[data-hero-section]'));
    return heroes;
  };

  const getNextSection = (hero) => {
    const shopifySection = hero.closest('.shopify-section');
    if (!shopifySection) return null;

    let next = shopifySection.nextElementSibling;
    while (next) {
      const isSection = next.classList?.contains('shopify-section');
      const isVisible = !next.hasAttribute('hidden') && next.offsetHeight > 0;
      if (isSection && isVisible) return next;
      next = next.nextElementSibling;
    }

    return null;
  };

  const scrollToNextSection = (hero) => {
    if (!hero) return;

    const nextSection = getNextSection(hero);
    const maxScrollTop = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
    let targetTop = 0;

    if (nextSection) {
      targetTop = window.scrollY + nextSection.getBoundingClientRect().top;
    } else {
      const heroRect = hero.getBoundingClientRect();
      targetTop = window.scrollY + heroRect.top + Math.max(heroRect.height * 0.9, window.innerHeight * 0.85);
    }

    window.scrollTo({
      top: clamp(targetTop, 0, maxScrollTop),
      behavior: reducedMotion.matches ? 'auto' : 'smooth',
    });
  };

  const onScrollIndicatorClick = (event) => {
    const trigger = event.target.closest('[data-hero-scroll-next]');
    if (!trigger) return;

    const hero = trigger.closest('[data-hero-section]');
    if (!hero) return;

    event.preventDefault();
    scrollToNextSection(hero);
  };

  const getHeroVideos = (hero) => {
    if (!hero?.querySelectorAll) return [];
    return Array.from(hero.querySelectorAll('.hero__video')).filter((node) => node instanceof HTMLVideoElement);
  };

  const isVideoHero = (hero) => hero?.dataset?.heroVideoEnabled === 'true';
  const isDeferredVideoHero = (hero) => hero?.dataset?.heroVideoDeferred === 'true' && isVideoHero(hero);

  const syncDecorativeVideoA11y = (hero) => {
    if (!isVideoHero(hero)) return;
    getHeroVideos(hero).forEach((video) => {
      video.setAttribute('aria-hidden', 'true');
      video.setAttribute('tabindex', '-1');
      video.muted = true;
      video.setAttribute('muted', '');
      video.playsInline = true;
      video.setAttribute('playsinline', '');
    });
  };

  const primeDeferredVideoHero = (hero) => {
    if (!isDeferredVideoHero(hero)) return;
    hero.dataset.heroVideoHydrated = 'false';

    getHeroVideos(hero).forEach((video) => {
      video.pause();
      video.autoplay = false;
      video.removeAttribute('autoplay');
      video.preload = 'none';
      video.setAttribute('preload', 'none');
    });
  };

  const activateDeferredVideoHero = (hero) => {
    if (!isDeferredVideoHero(hero)) return;
    if (hero.dataset.heroVideoHydrated === 'true') return;
    hero.dataset.heroVideoHydrated = 'true';

    getHeroVideos(hero).forEach((video) => {
      const startPlayback = () => {
        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {});
        }
      };

      video.preload = 'metadata';
      video.setAttribute('preload', 'metadata');

      if (video.readyState < 2) {
        video.load();
        video.addEventListener('loadedmetadata', startPlayback, { once: true });
        return;
      }

      startPlayback();
    });
  };

  const ensureVideoObserver = () => {
    if (videoObserver || typeof IntersectionObserver === 'undefined') return;

    videoObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting && entry.intersectionRatio <= 0) return;
          const hero = entry.target;
          activateDeferredVideoHero(hero);
          videoObserver?.unobserve(hero);
        });
      },
      {
        rootMargin: '220px 0px',
        threshold: [0, 0.2],
      }
    );
  };

  const observeDeferredVideoHero = (hero) => {
    if (!isDeferredVideoHero(hero)) return;

    primeDeferredVideoHero(hero);
    ensureVideoObserver();

    if (!videoObserver) {
      activateDeferredVideoHero(hero);
      return;
    }

    deferredVideoHeroes.add(hero);
    videoObserver.observe(hero);
  };

  const unobserveDeferredVideoHero = (hero) => {
    if (!hero) return;
    deferredVideoHeroes.delete(hero);
    videoObserver?.unobserve(hero);
  };

  const mount = (scope = document) => getHeroes(scope).forEach((hero) => {
    syncDecorativeVideoA11y(hero);

    if (isDeferredVideoHero(hero)) {
      observeDeferredVideoHero(hero);
      return;
    }

    unobserveDeferredVideoHero(hero);
  });

  const unmount = (scope = document) => getHeroes(scope).forEach((hero) => {
    unobserveDeferredVideoHero(hero);
  });

  document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', () => {
      mount(document);
    }, { once: true })
    : mount(document);

  document.addEventListener('click', onScrollIndicatorClick);
  document.addEventListener('shopify:section:load', (event) => {
    mount(event.target);
  });
  document.addEventListener('shopify:section:unload', (event) => {
    unmount(event.target);
  });

  window.ThemeHeroEffects = { initialized: true };
})();
